import { FC, Fragment, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import cls from 'clsx';
import {
  CALENDAR_COL,
  CALENDAR_ROW,
  WEEKDAYS,
  HOLIDAYS
} from '@link/share/constants';
import { convertDateToString } from '@link/share/utils';
import { MonthlyCalendarProps } from './MonthlyCalendar.types';
import {
  getDefaultTableCell,
  getFirstDayOfMonth,
  getMonthArray
} from './utils';
import styles from './monthly.module.css';

const MonthlyCalendar: FC<MonthlyCalendarProps> = ({
  className,
  startDate,
  renderCell,
  showWeekdays = true
}) => {
  const { t } = useTranslation();
  const startYear = useMemo(() => startDate.getFullYear(), [startDate]);
  const startMonth = useMemo(() => startDate.getMonth(), [startDate]);

  const getCellTitle = useCallback(
    (day: number) => {
      const cellDate = new Date(startYear, startMonth, day);
      return convertDateToString(cellDate);
    },
    [startYear, startMonth]
  );

  const tableCell = useMemo(() => {
    const month = getMonthArray(startYear, startMonth);
    const offset = getFirstDayOfMonth(startDate);
    const cell = getDefaultTableCell();

    for (let j = offset; j < CALENDAR_COL; j++) {
      const day = month.shift();
      if (day) {
        cell[0][j].value = day.toString();
        cell[0][j].title = getCellTitle(day);
        cell[0][j].week = WEEKDAYS[j % 7];
      }
    }

    for (let i = 1; i < CALENDAR_ROW; i++) {
      for (let j = 0; j < CALENDAR_COL; j++) {
        const day = month.shift();
        if (day) {
          cell[i][j].value = day.toString();
          cell[i][j].title = getCellTitle(day);
          cell[i][j].week = WEEKDAYS[j % 7];
        }
      }
    }

    // remove last empty row
    if (cell[CALENDAR_ROW - 1].every(cell => !cell.value)) {
      delete cell[CALENDAR_ROW - 1];
    }

    return cell;
  }, [startDate, startYear, startMonth, getCellTitle]);

  const actualRows = useMemo(() => {
    return Object.keys(tableCell).length;
  }, [tableCell]);

  return (
    <div
      className={cls(styles.calendar, className)}
      role="grid"
      style={{ '--calendar-rows': actualRows } as React.CSSProperties}
    >
      {showWeekdays && (
        <div className={styles.calendarWeek}>
          {WEEKDAYS.map((day, index) => {
            const isWeekend = index === 0 || index === 6; // Sunday (0) or Saturday (6)
            return (
              <div
                key={`${day}-${index}`}
                className={cls(
                  styles.calendarWeekItem,
                  isWeekend && styles.weekendHeader
                )}
              >
                {t(`share.weeks.${day}`)}
              </div>
            );
          })}
        </div>
      )}

      <div className={styles.calendarMonth}>
        {tableCell.flat().map((cell, index) => {
          const isHoliday = !!(
            cell.title && HOLIDAYS[cell.title as keyof typeof HOLIDAYS]
          );
          return (
            <Fragment key={index}>
              {renderCell({
                date: cell.value ? cell.title : '',
                day: cell.value || '',
                week: cell.week,
                isEmpty: !cell.value,
                isHoliday
              })}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default MonthlyCalendar;
